import en from "@/localization/en.json";
import { getWishlistUpdatedProducts } from "@/utils/helpers/wishlist";
import _ from "lodash";

import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { fetchAllBrands } from "repo/brand";
import { getSafeStaticProps } from "@/utils/helpers/isr.helpers";
import { useFetchSearchData, useFetchSortOptions } from "repo/search";
import { useFetchWishListInfo } from "repo/wishlist";
import { useSession } from "next-auth/react";
import { getPaginationResultText } from "@/utils/pagination";
import { usePagination } from "hooks/usePagination";
import { useSort } from "hooks/useSort";

import {
  FilterByBtn,
  SortByBtn,
  Breadcrumbs,
  CartSidebarWrapper,
  SortModal,
  HtmlRenderer,
  FourOFour,
  NotFound,
  CustomSelect,
  Loader,
  AddToCartModal,
  Pagination,
  Card,
  MetaData,
  ProductFilters,
  FilterMobileView,
} from "@/components/index.js";

function Brand({ brandData }) {
  const router = useRouter();
  const [searchInfo, setSearchInfo] = useState(brandData);
  const [rowData, setRowData] = useState(brandData?.attributes || []);
  const [brandProducts, setBrandProducts] = useState(brandData?.products);

  const { data: session } = useSession();
  const { getPaginateData } = usePagination();

  const { isFetching: isBrandDataFetching } = useFetchSearchData(
    router.asPath,
    {
      onSuccess: (data) => {
        setSearchInfo(data);
        setRowData(data.attributes);
        setBrandProducts(data.products);
      },
      enabled: Object.keys(router.query).length > 1,
    }
  );

  const { data: sortOptionData = [] } = useFetchSortOptions();

  const { sortData, setSelectedSortValue } = useSort(sortOptionData);

  const { refetch } = useFetchWishListInfo({
    enabled: false,
    onSuccess: (data) => {
      if (!data.length) return;
      if (!_.isEmpty(brandProducts)) {
        setBrandProducts(getWishlistUpdatedProducts(data, brandProducts));
      }
    },
  });

  useEffect(() => {
    if (session?.token) {
      refetch();
    }
  }, [session]);

  if (!searchInfo) {
    return <FourOFour />;
  }

  return (
    <>
      {isBrandDataFetching && <Loader />}
      {searchInfo && (
        <MetaData {...searchInfo.metadata} schema={searchInfo.metadata} />
      )}

      <div className="w-full sm:flex sm:justify-around pb-[1rem]">
        <div className="hidden sm:max-w-[15%] sm:w-[15%] sm:flex">
          <div className="sm:flex flex-col items-start w-full">
            <p className="text-grayD4 text-heading4 font-normal mt-[2rem]">
              {en["common.filter_by"]}
            </p>
            <div>
              <ProductFilters filterData={rowData} />
            </div>
          </div>
        </div>

        <div className="w-full sm:w-[65%] mt-[1.375rem]">
          <div className="pl-[1.25rem] sm:pl-0">
            <Breadcrumbs
              breadcrumbs={[{ value: searchInfo?.brand?.name, link: "" }]}
            />
          </div>

          <div className="sm:flex justify-between mt-[1.875rem] pl-[1.25rem] sm:pl-0 sm:pb-3">
            <div className="space-x-[0.75rem] sm:space-x-0  sm:space-y-[0.625rem] flex items-center sm:block">
              <p className="text-[#0A101A] text-[1.5rem] font-semibold">
                {searchInfo?.brand.name} products
              </p>

              <p className="text-[#414E5F] text-[0.75rem]">
                {getPaginationResultText(
                  searchInfo?.meta,
                  en["common.results"]
                )}
              </p>
            </div>

            <div className="hidden sm:flex items-center">
              <label className="text-[#7B838F]">{en["common.sort_by"]}</label>
              <CustomSelect
                options={sortOptionData}
                onChange={(state) => sortData(state)}
                value={setSelectedSortValue}
              />
            </div>
          </div>

          {/* start mobile screen sort filter options */}
          <div className="pb-[2rem] pt-[3rem] sm:hidden">
            <div className="flex items-center justify-center space-x-[1rem]">
              <FilterByBtn />
              <SortByBtn />
            </div>
            <FilterMobileView filterData={rowData} />
            <SortModal
              sortData={sortOptionData}
              onChange={(data) => sortData({ value: data })}
              value={setSelectedSortValue}
            />
          </div>

          {/* end mobile screen sort filter options */}

          {brandProducts && (
            <div className="w-full mx-auto grid grid-cols-2 gap-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
              {!!brandProducts.length &&
                brandProducts.map((product) => (
                  <Card product={product} key={product.slug} />
                ))}
            </div>
          )}

          {brandProducts && !brandProducts.length && (
            <NotFound
              imageHolderClassName="w-[15rem] md:w-[20rem] h-[11.25rem] md:h-[15rem]"
              imageName="no-product-found.png"
              wrapperClassName="mx-auto mt-[4.375rem] mb-[8.43rem]"
              heading=""
              bodyText="No product found!"
              bodyClassName="mt-[1.25rem] text-grayD3 text-body1 md:text-heading5 max-w-[23.5rem]"
            />
          )}
        </div>
      </div>
      <CartSidebarWrapper />
      {searchInfo?.meta?.total > searchInfo?.meta?.per_page && (
        <div className="mb-[5.625rem]">
          <Pagination
            total={Math.ceil(
              searchInfo?.meta?.total / searchInfo?.meta?.per_page
            )}
            currentPage={searchInfo?.meta?.current_page - 1}
            getPaginateData={(data) => getPaginateData(data)}
          />
        </div>
      )}

      <div className="mt-[5rem] mb-[5.656rem] w-full px-7 lg:px-[7.5rem]">
        <HtmlRenderer
          htmlData={searchInfo?.brand?.description}
          visibleLineClass="line-clamp-none"
        />
      </div>
      <AddToCartModal />
    </>
  );
}

export async function getStaticPaths() {
  const { data } = await fetchAllBrands();
  const { brands } = data;
  const paths = brands.map((brand) => ({ params: { brand_slug: brand.slug } }));

  return {
    paths,
    fallback: "blocking",
  };
}

export async function getStaticProps({ params }) {
  return getSafeStaticProps({
    url: `/v1/brands/${params.brand_slug}`,
    mapToProps: ({ data }) => (data ? { brandData: data } : null),
    fallbackProps: { brandData: null },
  });
}

export default Brand;
